import React, { useMemo } from 'react';
import { History } from 'lucide-react';
import { useMarket } from '../../context/MarketContext';

type Row = {
  side: 'BID' | 'ASK';
  price: number;
  old: number;
  new: number;
  delta: number;
};

type Props = {
  secId: string;
  maxPackets?: number; // optional cap
};

const fmtQty = (n: number) => n.toLocaleString();
const fmtPx = (p: number) => p.toFixed(2);

/** Convert a FIX-style timestamp to EAT (UTC+3).
 * Accepts:
 *  - "HH:mm:ss" or "HH:mm:ss.SSS" (interpreted as UTC today)
 *  - "YYYYMMDD-HH:mm:ss(.SSS)" or "YYYY-MM-DDTHH:mm:ss(.SSS)Z"
 */
function toEAT(ts?: string | number | Date | null): string {
  if (!ts) return '';

  // Convert to string if it's not already
  let tsStr: string;
  if (typeof ts === 'string') {
    tsStr = ts;
  } else if (typeof ts === 'number') {
    tsStr = new Date(ts).toISOString();
  } else if (ts instanceof Date) {
    tsStr = ts.toISOString();
  } else {
    tsStr = String(ts);
  }

  const eatOffsetMinutes = 3 * 60; // UTC+3

  // If it's a full ISO or has a date
  if (/\d{4}[-/]?\d{2}[-/]?\d{2}/.test(tsStr)) {
    // Normalize to a Date
    let d: Date | null = null;
    // Common FIX header like "20250904-17:26:59.625"
    const fixHeaderMatch = tsStr.match(/^(\d{4})(\d{2})(\d{2})-(\d{2}):(\d{2}):(\d{2})(\.\d+)?$/);
    if (fixHeaderMatch) {
      const [, Y, M, D, h, m, s] = fixHeaderMatch;
      d = new Date(Date.UTC(+Y, +M - 1, +D, +h, +m, +s));
    } else {
      // Try letting Date parse; treat as UTC if it ends with Z, otherwise append Z
      const iso = tsStr.endsWith('Z') ? tsStr : tsStr + 'Z';
      const parsed = new Date(iso);
      d = isNaN(+parsed) ? null : parsed;
    }

    if (d) {
      // add +3h
      d = new Date(d.getTime() + eatOffsetMinutes * 60 * 1000);
      const hh = String(d.getUTCHours()).padStart(2, '0');
      const mm = String(d.getUTCMinutes()).padStart(2, '0');
      const ss = String(d.getUTCSeconds()).padStart(2, '0');
      return `${hh}:${mm}:${ss} EAT`;
    }
  }

  // Time-only: "HH:mm:ss" or "HH:mm:ss.SSS"
  const timeMatch = tsStr.match(/^(\d{2}):(\d{2}):(\d{2})(\.\d+)?$/);
  if (timeMatch) {
    const [, h, m, s] = timeMatch;
    const now = new Date();
    // Treat the incoming as UTC time today
    const base = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), +h, +m, +s));
    const eat = new Date(base.getTime() + eatOffsetMinutes * 60 * 1000);
    const hh = String(eat.getUTCHours()).padStart(2, '0');
    const mm = String(eat.getUTCMinutes()).padStart(2, '0');
    const ss = String(eat.getUTCSeconds()).padStart(2, '0');
    return `${hh}:${mm}:${ss} EAT`;
  }

  // Fallback: show raw with EAT tag so you know it's unparsed
  return `${tsStr} EAT`;
}

/** Best effort numeric sort key from packet timestamp (UTC-based). */
function sortKeyFromTimestamp(ts?: string | number | Date | null): number {
  if (!ts) return 0;
  
  // Convert to string if it's not already
  let tsStr: string;
  if (typeof ts === 'string') {
    tsStr = ts;
  } else if (typeof ts === 'number') {
    // If it's a Unix timestamp (seconds), convert to milliseconds
    if (ts < 1e12) {
      return ts * 1000;
    }
    // Already in milliseconds
    return ts;
  } else if (ts instanceof Date) {
    return ts.getTime();
  } else {
    tsStr = String(ts);
  }
  
  // handle "YYYYMMDD-HH:mm:ss(.SSS)"
  const fixHeaderMatch = tsStr.match(/^(\d{4})(\d{2})(\d{2})-(\d{2}):(\d{2}):(\d{2})(\.\d+)?$/);
  if (fixHeaderMatch) {
    const [, Y, M, D, h, m, s] = fixHeaderMatch;
    return Date.UTC(+Y, +M - 1, +D, +h, +m, +s);
  }
  // time-only "HH:mm:ss(.SSS)" → treat as today UTC
  const timeMatch = tsStr.match(/^(\d{2}):(\d{2}):(\d{2})(\.\d+)?$/);
  if (timeMatch) {
    const [, h, m, s] = timeMatch;
    const now = new Date();
    return Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), +h, +m, +s);
  }
  // ISO-ish
  const d = new Date(tsStr.endsWith('Z') ? tsStr : tsStr + 'Z');
  return isNaN(+d) ? 0 : +d;
}

export default function BookChanges({ secId, maxPackets = 60 }: Props) {
  const { bookChanges } = useMarket();

  // 1) packets for the selected security (latest first, capped)
  const packetsForSec = useMemo(() => {
    const all = bookChanges ?? [];
    const secIdUpper = secId?.toUpperCase();
    // console.log(`[BookChanges] Filtering for ${secIdUpper}, total packets: ${all.length}`);
    const filtered = all.filter(pkt => {
      const match = pkt?.secId?.toUpperCase() === secIdUpper;
      if (!match && pkt?.secId) {
        // console.log(`[BookChanges] Skipping packet for ${pkt.secId} (looking for ${secIdUpper})`);
      }
      return match;
    });
    // console.log(`[BookChanges] Found ${filtered.length} packets for ${secIdUpper}`);
    // Defensively sort newest→oldest using parsed timestamps; if equal/absent, keep original order
    const sorted = [...filtered].sort((a, b) => sortKeyFromTimestamp(b?.timestamp) - sortKeyFromTimestamp(a?.timestamp));
    const result = sorted.slice(0, maxPackets);
    // console.log(`[BookChanges] Returning ${result.length} packets (max ${maxPackets})`);
    if (result.length > 0) {
      // console.log(`[BookChanges] Sample packet:`, { secId: result[0].secId, changesCount: result[0].changes?.length, timestamp: result[0].timestamp });
    }
    return result;
  }, [bookChanges, secId, maxPackets]);

  // 2) flatten rows that changed (old !== new), compute delta, sort per packet
  const rowsByPacket = useMemo(() => {
    return packetsForSec.map(pkt => {
      const rows: Row[] = (pkt?.changes ?? [])
        .map((c) => {
          const oldVal =
            Number.isFinite((c as any).old) ? Number((c as any).old) :
            Number.isFinite((c as any).previous) ? Number((c as any).previous) :
            Number.isFinite((c as any).prev) ? Number((c as any).prev) : NaN;

          const newVal =
            Number.isFinite((c as any).new) ? Number((c as any).new) :
            Number.isFinite((c as any).size) ? Number((c as any).size) : NaN;

          if (!Number.isFinite(oldVal) || !Number.isFinite(newVal)) return null;
          if (oldVal === newVal) return null;

          return {
            side: (c as any).side as Row['side'],
            price: Number((c as any).price),
            old: oldVal,
            new: newVal,
            delta: newVal - oldVal,
          } as Row;
        })
        .filter((x): x is Row => !!x)
        .sort((a, b) => {
          if (a.side !== b.side) return a.side === 'BID' ? -1 : 1;
          if (a.side === 'BID') return b.price - a.price;
          return a.price - b.price;
        });

      return { pkt, rows };
    });
  }, [packetsForSec]);

  return (
    <div style={{ background:'#1e293b', padding:20, borderRadius:8, border:'1px solid #334155', maxHeight:'625px', display:'flex', flexDirection:'column' }}>
      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:16 }}>
        <History size={16} color="#94a3b8" />
        <h3 style={{ margin:0, fontSize:16, fontWeight:600 }}>Order Book Changes — {secId}</h3>
        <div style={{ marginLeft:'auto', fontSize:11, color:'#64748b' }}>
          {packetsForSec.length ? `Last ${packetsForSec.length} packets` : '—'}
        </div>
      </div>

      <div style={{ maxHeight:'100%', overflowY:'auto' }}>
        {rowsByPacket.length === 0 ? (
          <div style={{ color:'#94a3b8', fontSize:12, textAlign:'center', padding:20 }}>
            No book changes yet for <strong>{secId}</strong>.
          </div>
        ) : (
          rowsByPacket.map(({ pkt, rows }, i) => (
            <div key={i} style={{ marginBottom:12, padding:12, background:'#0f172a', borderRadius:6, border:'1px solid #334155' }}>
              <div style={{ color:'#94a3b8', marginBottom:8, fontSize:12, display:'flex', gap:8, flexWrap:'wrap' }}>
                <span>{toEAT(pkt?.timestamp)}</span>
                <span>| SecID: <strong>{pkt?.secId}</strong></span>
                {pkt?.symbol ? <span>| Symbol: <strong>{pkt.symbol}</strong></span> : null}
                <span>| Changed Levels: {rows.length}</span>
              </div>

              {rows.length === 0 ? (
                <div style={{ color:'#64748b', fontSize:12 }}>No size changes in this packet.</div>
              ) : (
                <div style={{ display:'grid', gridTemplateColumns:'70px 1fr 1fr 1fr 1fr', gap:8 }}>
                  <div style={{ color:'#94a3b8', fontSize:11 }}>Side</div>
                  <div style={{ color:'#94a3b8', fontSize:11 }}>Price</div>
                  <div style={{ color:'#94a3b8', fontSize:11, textAlign:'right' }}>Old</div>
                  <div style={{ color:'#94a3b8', fontSize:11, textAlign:'right' }}>New</div>
                  <div style={{ color:'#94a3b8', fontSize:11, textAlign:'right' }}>Change</div>

                  {rows.map((r, j) => {
                    const changeColor = r.delta > 0 ? '#10b981' : r.delta < 0 ? '#ef4444' : '#94a3b8';
                    const sideColor = r.side === 'BID' ? '#10b981' : '#ef4444';
                    const changeStr = (r.delta > 0 ? '+' : '') + fmtQty(r.delta);
                    return (
                      <React.Fragment key={j}>
                        <div style={{ color: sideColor, fontWeight:700 }}>{r.side}</div>
                        <div style={{ color:'#e2e8f0' }}>{fmtPx(r.price)}</div>
                        <div style={{ color:'#94a3b8', textAlign:'right' }}>{fmtQty(r.old)}</div>
                        <div style={{ color:'#e2e8f0', textAlign:'right', fontWeight:600 }}>{fmtQty(r.new)}</div>
                        <div style={{ color: changeColor, textAlign:'right', fontWeight:700 }}>{changeStr}</div>
                      </React.Fragment>
                    );
                  })}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
